import { InputNumber } from '@aigc/antd';
import { AiDrawer } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay, useOverlayRef } from '@aigc/overlay-hook';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Form,
  Input,
  Space,
  Table,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { number, object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { queryUserCredits, updateUserCredit } from '../api';
import type { UserCreditItem } from '../model';

const credit_schema = object({
  credit: number().min(-1, '额度不能小于 -1').required('请输入信用点额度'),
});

const search_schema = object({
  keyword: string().trim().nullable().default(null),
});

function renderCredit(credit: number): string {
  if (credit === -1) return '无限';
  if (credit === 0) return '无额度';
  return String(credit);
}

function EditUserCreditDrawer(props: { row: UserCreditItem }) {
  const overlay_ref = useOverlayRef();
  const { message } = App.useApp();

  const form = useForm({
    mode: 'onChange',
    defaultValues: { credit: props.row.credit },
    resolver: yupResolver(credit_schema),
  });

  const update = useMutation(updateUserCredit(props.row.name));

  const onCancel = () => {
    overlay_ref.close();
  };

  return (
    <AiDrawer
      title="修改用户信用点额度"
      onClose={onCancel}
      footer={
        <div className="flex items-center">
          <div className="flex-auto"></div>
          <Space>
            <Button variant="outlined" color="default" onClick={onCancel}>
              取消
            </Button>
            <Button
              variant="solid"
              color="primary"
              loading={update.isPending}
              onClick={form.handleSubmit(async ({ credit }) => {
                if (update.isPending) {
                  return;
                }
                await update.mutateAsync({ credit });
                message.success('修改成功');
                overlay_ref.close();
              })}
            >
              保存
            </Button>
          </Space>
        </div>
      }
    >
      <Form layout="vertical">
        <Form.Item
          label="每日信用点额度"
          required
          extra="0=无额度，-1=无限额度，正整数=每日额度"
        >
          <Controller
            control={form.control}
            name="credit"
            render={({ field }) => (
              <InputNumber
                {...field}
                classNames={{ root: 'w-full' }}
                min={-1}
                precision={0}
              />
            )}
          />
        </Form.Item>
      </Form>
    </AiDrawer>
  );
}

export function UserCreditTable() {
  const form = useForm({
    defaultValues: { keyword: null as string | null },
    resolver: yupResolver(search_schema),
  });

  const { pagination, queryParams, queryMany, changePageNumber } =
    usePagingQueryHelper<{ keyword: string | null }>({
      defaultPagination: { page: 1, size: 100 },
      defaultQueryParams: { keyword: null },
      onRefresh: () => refetch(),
    });

  const { data, isFetching, refetch } = useQuery({
    ...queryUserCredits({
      pagination,
      keyword: queryParams?.keyword || undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const handleQuery = form.handleSubmit((values) => queryMany(values));
  const handleReset = () => {
    form.reset({ keyword: null });
    queryMany({ keyword: null });
  };

  const columns = useMemo<TableColumnsType<UserCreditItem>>(
    () => [
      {
        title: '用户',
        fixed: 'left',
        width: 200,
        key: 'name',
        render: (_, row) => row.name,
      },
      {
        title: '每日信用点',
        key: 'credit',
        render: (_, row) => (
          <Typography.Text type={row.credit === 0 ? 'danger' : undefined}>
            {renderCredit(row.credit)}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 80,
        fixed: 'right',
        key: 'action',
        render: (_, row) => {
          return (
            <Space size={8}>
              <UpdateButton row={row}></UpdateButton>
            </Space>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="h-full overflow-auto bg-white p-4">
      <div className="mb-4">
        <Typography.Title level={4} style={{ margin: 0 }}>
          信用点额度
        </Typography.Title>
      </div>
      <Form className="!mb-4" layout="inline" onFinish={handleQuery}>
        <FormControl
          name="keyword"
          label="用户"
          control={form.control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              placeholder="请输入账户名称"
              allowClear
            />
          )}
        />
        <Space>
          <Button htmlType="submit" variant="solid" color="primary">
            查询
          </Button>
          <Button variant="outlined" color="default" onClick={handleReset}>
            重置
          </Button>
        </Space>
      </Form>
      <Table
        rowKey={(record) => record.id}
        columns={columns}
        loading={isFetching}
        dataSource={data?.items}
        pagination={{
          total: data?.total,
          current: pagination.page,
          pageSize: pagination.size,
          showSizeChanger: false,
          onChange: changePageNumber,
        }}
      />
    </div>
  );
}

const UpdateButton = (props: { row: UserCreditItem }) => {
  const [openOverlay] = useOverlay();

  const handleOpenEditModal = () => {
    openOverlay(<EditUserCreditDrawer row={props.row} />);
  };

  return (
    <Button
      size="small"
      variant="filled"
      color="default"
      onClick={() => handleOpenEditModal()}
    >
      修改
    </Button>
  );
};
